<template>
    <TriadModal ref="modal" title="Search Image" :size="'medium'">
        <div class="flex flex-col gap-4 min-h-[400px]">
                <!-- Navigation Tabs -->
                <div class="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                    <button 
                        @click="activeTab = 'search'"
                        :class="[
                            'px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap',
                            activeTab === 'search' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        ]"
                    >
                        {{ $t('playlistImageCard.pexelsTab', 'Search on Pexels.com') }}
                        <div v-if="activeTab === 'search'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 dark:bg-indigo-400"></div>
                    </button>
                    <button 
                        @click="activeTab = 'upload'"
                        :class="[
                            'px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap',
                            activeTab === 'upload' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        ]"
                    >
                        {{ $t('playlistImageCard.uploadTab', 'Upload Image') }}
                        <div v-if="activeTab === 'upload'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 dark:bg-indigo-400"></div>
                    </button>
                    <button 
                        v-if="hasAiFeature"
                        @click="activeTab = 'ai'"
                        :class="[
                            'px-4 py-2 font-medium text-sm transition-colors relative flex items-center gap-1.5 whitespace-nowrap',
                            activeTab === 'ai' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        ]"
                    >
                        <SparklesIcon class="w-4 h-4 text-indigo-500" />
                        <span>{{ $t('playlistImageCard.aiTab', 'Generate with AI') }}</span>
                        <div v-if="activeTab === 'ai'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 dark:bg-indigo-400"></div>
                    </button>
                </div>

                <!-- Search & API Key Configuration -->
                <div v-if="activeTab === 'search'" class="flex flex-col gap-2">
                    <div class="flex gap-2">
                        <input 
                            v-model="searchQuery" 
                            @keyup.enter="searchImages"
                            type="text" 
                            placeholder="Search for photos..." 
                            class="flex-grow p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <Button 
                            @click="searchImages" 
                            :disabled="loading"
                            class="!ms-0"
                        >
                            Search
                        </Button>
                    </div>
                </div>

                <!-- Upload Section -->
                <div v-else-if="activeTab === 'upload'" class="flex flex-col gap-2">
                     <TriadFileInput 
                        v-if="props.id"
                        :id="props.id" 
                        :direction="'gallery'" 
                        @selected="selectImage"
                    />
                    <div v-else class="text-center text-gray-500 py-8">
                        No ID provided for file upload.
                    </div>
                </div>

                <!-- AI Generation Section -->
                <div v-else-if="activeTab === 'ai'" class="flex flex-col gap-4">
                    <div class="flex flex-col gap-1.5">
                        <label class="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            {{ $t('playlistImageCard.promptLabel', 'Image description (optional)') }}
                        </label>
                        <textarea
                            v-model="aiPrompt"
                            rows="3"
                            :placeholder="$t('playlistImageCard.promptPlaceholder', 'Describe the cover you want, or leave blank to auto-generate from playlist songs and tags...')"
                            class="w-full p-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                        ></textarea>
                        <span class="text-[11px] text-gray-400 dark:text-gray-500">
                            {{ $t('playlistImageCard.autoContextHint', 'Automatically uses playlist name, description, tags, and songs when left blank.') }}
                        </span>
                    </div>

                    <div class="flex flex-col gap-1.5">
                        <label class="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            {{ $t('playlistImageCard.styleLabel', 'Visual style') }}
                        </label>
                        <div class="flex flex-wrap gap-1.5">
                            <button
                                v-for="styleOpt in styleOptions"
                                :key="styleOpt.id"
                                type="button"
                                @click="aiStyle = styleOpt.id"
                                :class="[
                                    'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors cursor-pointer',
                                    aiStyle === styleOpt.id
                                        ? 'bg-indigo-50 border-indigo-500 text-indigo-700 dark:bg-indigo-900/40 dark:border-indigo-400 dark:text-indigo-300'
                                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
                                ]"
                            >
                                {{ $t(`playlistImageCard.styles.${styleOpt.id}`, styleOpt.label) }}
                            </button>
                        </div>
                    </div>

                    <div class="flex flex-col sm:flex-row sm:items-end gap-3">
                        <div class="flex flex-col gap-1.5 flex-grow">
                            <label class="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                {{ $t('playlistImageCard.modelLabel', 'AI Model') }}
                            </label>
                            <select
                                v-model="aiModel"
                                class="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="qwen/qwen-image-2.1-turbo">
                                    {{ $t('playlistImageCard.modelTurbo', 'Qwen Image 2.1 Turbo (Best Value • Fast)') }}
                                </option>
                                <option value="meta/muse-image">
                                    {{ $t('playlistImageCard.modelMuse', 'Meta: Muse Image (Agentic)') }}
                                </option>
                                <option value="qwen/qwen-image-3">
                                    {{ $t('playlistImageCard.modelQwen3', 'Qwen Image 3 (Balanced • Next-Gen)') }}
                                </option>
                                <option value="qwen/qwen-image-2.1-pro">
                                    {{ $t('playlistImageCard.modelPro', 'Qwen Image 2.1 Pro (Maximum Fidelity)') }}
                                </option>
                            </select>
                        </div>

                        <Button
                            @click="generateAiCover"
                            :disabled="aiLoading"
                            class="!ms-0 flex items-center justify-center gap-2 h-[38px]"
                        >
                            <ArrowPathIcon v-if="aiLoading" class="w-4 h-4 animate-spin" />
                            <SparklesIcon v-else class="w-4 h-4" />
                            <span>{{ aiLoading ? $t('playlistImageCard.generating', 'Generating...') : $t('playlistImageCard.generateBtn', 'Generate Cover') }}</span>
                        </Button>
                    </div>

                    <!-- AI Loading Skeleton -->
                    <div v-if="aiLoading" class="w-full aspect-video rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center gap-3 p-6 animate-pulse">
                        <ArrowPathIcon class="w-8 h-8 text-indigo-500 animate-spin" />
                        <span class="text-xs font-medium text-gray-500 dark:text-gray-400">
                            {{ $t('playlistImageCard.generating', 'Generating image with AI...') }}
                        </span>
                    </div>

                    <!-- AI Generated Preview -->
                    <div v-else-if="aiGeneratedUrl" class="flex flex-col gap-3">
                        <div class="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                            <img
                                :src="aiGeneratedUrl"
                                alt="AI Generated Cover"
                                class="w-full h-full object-cover"
                            />
                        </div>
                        <div class="flex justify-end gap-2">
                            <button
                                type="button"
                                @click="generateAiCover"
                                :disabled="aiLoading"
                                class="px-3.5 py-2 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5 cursor-pointer"
                            >
                                <ArrowPathIcon class="w-4 h-4" />
                                <span>{{ $t('playlistImageCard.regenerate', 'Generate another') }}</span>
                            </button>
                            <button
                                type="button"
                                @click="selectImage({ url: aiGeneratedUrl })"
                                class="px-4 py-2 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors flex items-center gap-1.5 shadow-sm cursor-pointer"
                            >
                                <span>{{ $t('playlistImageCard.useImage', 'Use this cover') }}</span>
                            </button>
                        </div>
                    </div>
                </div>

            <!-- Loading State (Initial Load) -->
            <div v-if="loading && activeTab === 'search' && images.length === 0" class="flex justify-center items-center py-8">
                <Load class="w-8 h-8"/>
            </div>

            <!-- Results Grid -->
            <div v-else-if="images.length > 0 && activeTab === 'search'" class="flex-grow overflow-y-auto max-h-[60vh]">
                <div class="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div 
                        v-for="img in images" 
                        :key="img.id" 
                        @click="selectImage(img)"
                        class="relative group cursor-pointer aspect-square rounded-lg overflow-hidden bg-gray-100"
                    >
                        <img 
                            :src="img.src.medium" 
                            :alt="img.alt" 
                            class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div class="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <span class="text-white opacity-0 group-hover:opacity-100 font-medium px-3 py-1 bg-black/50 rounded-full text-sm">
                                Select
                            </span>
                        </div>
                        <a 
                            :href="img.url" 
                            target="_blank" 
                            @click.stop 
                            class="absolute bottom-1 right-1 text-[10px] text-white/80 hover:text-white bg-black/30 px-1 rounded z-10"
                        >
                            {{ img.photographer }}
                        </a>
                    </div>
                </div>
                <div class="flex justify-center mt-4 pb-4" v-if="hasMore">
                     <Button 
                        @click="loadMore" 
                        :disabled="loading"
                        type="secondary"
                        class="!text-indigo-600 dark:!text-indigo-400 !border-indigo-600 dark:!border-indigo-400 hover:!bg-indigo-50 dark:hover:!bg-indigo-950/30 !ms-0 flex items-center gap-2"
                    >
                        <Load v-if="loading" class="w-4 h-4"/>
                        <span v-if="!loading">Load More</span>
                        <span v-else>Loading...</span>
                    </Button>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="searched && !loading && activeTab === 'search'" class="flex flex-col items-center justify-center py-8 text-gray-500">
                <p>No images found. Try a different search term.</p>
            </div>
             <div v-else-if="activeTab === 'search'" class="flex flex-col items-center justify-center py-8 text-gray-400">
                <p>Search for high quality free photos</p>
            </div>
        </div>
    </TriadModal>
</template>

<script setup>
import { ref, computed, defineExpose, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import { SparklesIcon, ArrowPathIcon } from '@heroicons/vue/24/outline';
import TriadModal from '@/components/common/TriadModal.vue';
import Load from '@/components/TriadLoad.vue';
import TriadFileInput from '@/components/form/TriadFileInput.vue';
import Button from '@/components/form/TriadButton.vue';
import playlistService from '@/service/playlist.service';
import userStore from '@/store/user.store';
import toastStore from '@/store/toast.store';

const { t } = useI18n();
const props = defineProps(['id', 'playlist']);
const modal = ref(null);
const searchQuery = ref('');
const images = ref([]);
const loading = ref(false);
const searched = ref(false);
const page = ref(1);
const hasMore = ref(true);
const activeTab = ref('search');

// AI Cover state
const aiPrompt = ref('');
const aiStyle = ref('cinematic');
const aiModel = ref('qwen/qwen-image-2.1-turbo');
const aiLoading = ref(false);
const aiGeneratedUrl = ref(null);

const styleOptions = [
    { id: 'cinematic', label: 'Cinematic' },
    { id: 'worship', label: 'Stage / Worship' },
    { id: 'acoustic', label: 'Acoustic' },
    { id: 'minimalist', label: 'Minimalist' },
    { id: 'abstract', label: 'Abstract' },
    { id: 'vintage', label: 'Vintage / Vinyl' }
];

const hasAiFeature = computed(() => Boolean(userStore.getters?.hasAiFeature));

let resolvePromise = null;

const currentSearchTerm = ref('');

const open = (initialTab = null) => {
    if (initialTab && ['search', 'upload', 'ai'].includes(initialTab)) {
        activeTab.value = initialTab;
    }
    modal.value.open();
    return new Promise((resolve) => {
        resolvePromise = resolve;
    });
};

const close = () => {
    modal.value.close();
    resolvePromise = null;
};

const generateAiCover = async () => {
    if (aiLoading.value) return;
    aiLoading.value = true;

    try {
        const pl = props.playlist || {};
        const payload = {
            playlistId: props.id || pl._id || pl.id || null,
            prompt: aiPrompt.value,
            name: pl.name || '',
            description: pl.description || '',
            tags: (pl.tags || []).map(tag => (typeof tag === 'string' ? tag : tag.name)).filter(Boolean),
            songs: (pl.songs || []).slice(0, 8).map(s => ({
                title: s.title || s.name || ''
            })),
            style: aiStyle.value,
            model: aiModel.value,
            aspectRatio: '16:9'
        };

        const response = await playlistService.generateCover(payload);
        if (!response.ok) {
            throw new Error('Failed to generate cover');
        }

        const data = await response.json();
        if (data && data.url) {
            aiGeneratedUrl.value = data.url;
            toastStore.dispatch('addMessage', {
                message: t('playlistImageCard.generatedSuccess', 'AI cover generated successfully!'),
                icon: 'success'
            });
        } else {
            throw new Error('No image URL returned');
        }
    } catch (error) {
        console.error('AI Cover Generation Error:', error);
        toastStore.dispatch('addMessage', {
            message: t('playlistImageCard.generateError', 'Failed to generate cover with AI'),
            icon: 'error'
        });
    } finally {
        aiLoading.value = false;
    }
};

const searchImages = async (isLoadMore = false) => {
    if (!searchQuery.value.trim()) return;

    // If it's a new search (not load more)
    if (!isLoadMore) {
        // If query hasn't changed and we already have results/searched, do nothing
        if (searchQuery.value === currentSearchTerm.value && searched.value) {
            return;
        }
        
        // New search query
        currentSearchTerm.value = searchQuery.value;
        page.value = 1;
        images.value = [];
        searched.value = false;
    }

    loading.value = true;
    try {
        const response = await fetch(`/api/media/search?q=${encodeURIComponent(isLoadMore ? currentSearchTerm.value : searchQuery.value)}&per_page=15&page=${page.value}`);

        if (!response.ok) {
            throw new Error('Failed to fetch images');
        }

        const data = await response.json();
        
        if (isLoadMore) {
            images.value = [...images.value, ...data.photos];
        } else {
            images.value = data.photos;
        }
        
        hasMore.value = data.photos.length === 15; // Assuming per_page is 15
        searched.value = true;
    } catch (error) {
        console.error('Pexels API Error:', error);
        // Could add toast notification here
    } finally {
        loading.value = false;
    }
};

const loadMore = () => {
    page.value++;
    searchImages(true);
};

const selectImage = (img) => {
    if (resolvePromise) {
        // Return preferred size, e.g., large or medium, or the direct URL if it's an uploaded file
        const url = img.src ? (img.src.large2x || img.src.large || img.src.medium) : img.url;
        resolvePromise(url);
    }
    close();
};

defineExpose({ open });
</script>
